import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import {
  FileUploadState,
  RedoFileUpload,
} from "@redotech/arbiter/file-upload/redo-file-upload";
import { Flex } from "@redotech/arbiter/flex";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { toast } from "@redotech/redo-web/alert";
import { memo, useContext, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../../../team";
import { RateGridPreview } from "../components/rate-grid-preview";
import { useRateCalculator } from "../hooks";
import { buildDummyRateGridFromUspsMarketRates } from "../utils/pricing-calculator";
import * as styles from "./data-input-step.module.css";

interface DataInputStepProps {
  canFetchHistoricalData?: boolean;
  teamId?: string | null;
}

export const DataInputStep = memo(function DataInputStep({
  canFetchHistoricalData = true,
  teamId: teamIdProp,
}: DataInputStepProps) {
  const { state, dispatch } = useRateCalculator();
  const teamFromContext = useContext(TeamContext);
  const client = useRequiredContext(RedoAdminRpcClientContext);

  const teamId = teamIdProp ?? teamFromContext?._id;
  const teamName = teamFromContext?.name ?? "the team";

  const [files, setFiles] = useState<FileUploadState<File>[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleFilesAdded = useHandler(async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) {
      return;
    }
    const file = fileList[0];
    if (!file) {
      return;
    }

    setFiles([{ name: file.name, bytesTotal: file.size, value: file }]);
    setIsLoading(true);

    try {
      const result = await client.parseCarrierInvoiceCsv(
        {
          teamId: teamId ?? null,
          originZip: state.merchantAddress?.postalCode,
        },
        { files: [file] },
      );

      if (!result.success || !result.rateGrid) {
        dispatch({
          type: "SET_DATA_ERROR",
          error: result.error || "Failed to parse file",
        });
        toast("Failed to parse file", { variant: "error" });
        setIsLoading(false);
        return;
      }

      dispatch({ type: "SET_DATA_SOURCE", source: "upload" });
      dispatch({
        type: "SET_RATE_DATA",
        rateGrid: result.rateGrid,
        serviceLevelStats: result.serviceLevelStats ?? null,
        totalShipments: result.totalShipments ?? 0,
        oldestShipmentDate: result.oldestShipmentDate ?? null,
        newestShipmentDate: result.newestShipmentDate ?? null,
        destinationZips: result.destinationZips,
        uploadedFile: file,
        uploadedFileUrl: result.uploadedFileUrl,
      });
      dispatch({ type: "SET_STEP", step: 3 as const });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      dispatch({ type: "SET_DATA_ERROR", error: message });
      toast(`Error: ${message}`, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  });

  const handleFetchHistorical = useHandler(async () => {
    if (!teamId) {
      toast("No team selected", { variant: "error" });
      return;
    }

    setIsLoading(true);
    dispatch({ type: "SET_DATA_SOURCE", source: "historical" });

    try {
      const result = await client.getHistoricalShipmentRates({ teamId });

      if (!result.success || !result.rateGrid) {
        const error = result.error || "No historical data available";
        dispatch({ type: "SET_DATA_ERROR", error });
        toast(error, { variant: "error" });
        setIsLoading(false);
        return;
      }

      dispatch({
        type: "SET_RATE_DATA",
        rateGrid: result.rateGrid,
        serviceLevelStats: result.serviceLevelStats,
        totalShipments: result.totalShipments,
        oldestShipmentDate: result.oldestShipmentDate,
        newestShipmentDate: result.newestShipmentDate,
      });
      dispatch({ type: "SET_STEP", step: 3 as const });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      dispatch({ type: "SET_DATA_ERROR", error: message });
      toast(`Error: ${message}`, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  });

  const handleFileDeleted = useHandler(() => {
    setFiles([]);
    dispatch({ type: "SET_DATA_ERROR", error: null });
  });

  const handleUseUspsMarketRates = useHandler(() => {
    const dummyGrid = buildDummyRateGridFromUspsMarketRates(
      state.baselineRateTables,
    );
    if (!dummyGrid) {
      toast("USPS market rate table not available", { variant: "error" });
      return;
    }
    dispatch({ type: "SET_DATA_SOURCE", source: "usps_market_rates" });
    dispatch({
      type: "SET_RATE_DATA",
      rateGrid: dummyGrid,
      serviceLevelStats: null,
      totalShipments: dummyGrid.weightBucketsOz.length * dummyGrid.zones.length,
      oldestShipmentDate: null,
      newestShipmentDate: null,
    });
    dispatch({ type: "SET_STEP", step: 2 as const });
  });

  if (state.rateGrid) {
    return (
      <Flex dir="column" gap="lg">
        <Flex dir="column" gap="sm">
          <Text fontSize="lg" fontWeight="semibold">
            Data Loaded
          </Text>
          <Text fontSize="sm" textColor="secondary">
            {state.dataSource === "upload"
              ? "Parsed from uploaded invoice"
              : state.dataSource === "usps_market_rates"
                ? "Using USPS market rates as baseline"
                : "Fetched from historical shipments"}
          </Text>
        </Flex>

        {state.serviceLevelStats && (
          <Flex gap="lg">
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium">
                Standard (Ground)
              </Text>
              <Text fontSize="xl" fontWeight="semibold">
                {state.serviceLevelStats.Standard.toLocaleString()}
              </Text>
            </Flex>
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium">
                Plus (Priority)
              </Text>
              <Text fontSize="xl" fontWeight="semibold">
                {state.serviceLevelStats.Plus.toLocaleString()}
              </Text>
            </Flex>
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium">
                Express
              </Text>
              <Text fontSize="xl" fontWeight="semibold">
                {state.serviceLevelStats.Express.toLocaleString()}
              </Text>
            </Flex>
          </Flex>
        )}

        <RateGridPreview rateGrid={state.rateGrid} />

        <Flex gap="md" justify="space-between">
          <RedoButton
            hierarchy="secondary"
            onClick={() => dispatch({ type: "SET_STEP", step: 1 })}
            size="md"
            text="Back"
          />
          <Flex gap="md">
            <RedoButton
              hierarchy="secondary"
              onClick={() => {
                dispatch({ type: "RESET" });
                setFiles([]);
              }}
              size="md"
              text="Start over"
            />
            <RedoButton
              hierarchy="primary"
              onClick={() => dispatch({ type: "SET_STEP", step: 3 })}
              size="md"
              text="Continue"
            />
          </Flex>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="xl">
      <Flex dir="column" gap="sm">
        <Text fontSize="lg" fontWeight="semibold">
          Select Data Source
        </Text>
        <Text fontSize="sm" textColor="secondary">
          Upload a carrier invoice CSV/Excel file or fetch the merchant's
          historical shipment data from the last 90 days.
        </Text>
      </Flex>

      <Flex dir="column" gap="lg">
        <Flex
          borderColor="primary"
          borderRadius="lg"
          borderWidth="1px"
          dir="column"
          gap="md"
          p="lg"
        >
          <Flex dir="column" gap="xs">
            <Text fontSize="md" fontWeight="semibold">
              Upload Invoice
            </Text>
            <Text fontSize="sm" textColor="secondary">
              Upload a CSV or Excel file containing carrier rates with weight,
              zone, and price columns.
            </Text>
          </Flex>

          <RedoFileUpload
            accept=".csv,.xlsx,.xls"
            acceptMessage="CSV or Excel files"
            disabled={isLoading}
            files={files}
            fullWidth
            handleFileDeleted={handleFileDeleted}
            handleFilesAdded={handleFilesAdded}
            hideDropzoneAfterSelection
          />
        </Flex>

        {canFetchHistoricalData && (
          <>
            <Flex align="center" gap="md" justify="center">
              <Flex bgColor="disabled" className={styles.dividerLine} />
              <Text fontSize="sm" fontWeight="medium" textColor="secondary">
                OR
              </Text>
              <Flex bgColor="disabled" className={styles.dividerLine} />
            </Flex>

            <Flex
              borderColor="primary"
              borderRadius="lg"
              borderWidth="1px"
              dir="column"
              gap="md"
              p="lg"
            >
              <Flex dir="column" gap="xs">
                <Text fontSize="md" fontWeight="semibold">
                  Historical Data
                </Text>
                <Text fontSize="sm" textColor="secondary">
                  Fetch the last 90 days of shipment data for {teamName} from
                  Snowflake.
                </Text>
              </Flex>

              <RedoButton
                hierarchy="secondary"
                onClick={handleFetchHistorical}
                pending={isLoading && state.dataSource === "historical"}
                size="md"
                text="Fetch Historical Data"
              />
            </Flex>

            <Flex align="center" gap="md" justify="center">
              <Flex bgColor="disabled" className={styles.dividerLine} />
              <Text fontSize="sm" fontWeight="medium" textColor="secondary">
                OR
              </Text>
              <Flex bgColor="disabled" className={styles.dividerLine} />
            </Flex>
          </>
        )}

        <Flex
          borderColor="primary"
          borderRadius="lg"
          borderWidth="1px"
          dir="column"
          gap="md"
          p="lg"
        >
          <Flex dir="column" gap="xs">
            <Text fontSize="md" fontWeight="semibold">
              No Invoice (USPS Market Rates)
            </Text>
            <Text fontSize="sm" textColor="secondary">
              Use USPS market rates as the baseline. For brands currently on
              USPS market rates without an invoice to upload.
            </Text>
          </Flex>

          <RedoButton
            hierarchy="secondary"
            onClick={handleUseUspsMarketRates}
            size="md"
            text="Use USPS Market Rates"
          />
        </Flex>
      </Flex>

      {state.dataError && (
        <Flex borderRadius="lg" className={styles.errorBackground} p="md">
          <Text fontSize="sm" textColor="primary">
            {state.dataError}
          </Text>
        </Flex>
      )}

      <Flex gap="md" justify="flex-start">
        <RedoButton
          hierarchy="secondary"
          onClick={() => dispatch({ type: "SET_STEP", step: 1 })}
          size="md"
          text="Back"
        />
      </Flex>
    </Flex>
  );
});
